import { Button, IdOptionType, SelectList, useModalContentHeader } from "shared";
import type { ILegalOrgListItem } from "entities/legal_orgs";
import React, { ReactNode, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useComponentsTranslations } from "../../../../../lib/i18n";
import { filterOrgsByScopeKey, type CompanyListScopeMode } from "../../../../../lib/utils/legal_orgs_scope_selection";
import styles from "./companies_configuration_modal.module.scss";

export type CompaniesConfigurationScope = CompanyListScopeMode;

interface ICompaniesConfigurationModalProps {
    close: () => void;
    scope: CompaniesConfigurationScope;
    organizations: ILegalOrgListItem[]
    scopeKey: string;
    scopeLabel: string;
    excludedCompanyIds: string[];
    onSave: (excludedYaIds: string[]) => void;
}

export const CompaniesConfigurationModal: React.FC<ICompaniesConfigurationModalProps> = React.memo(
    ({ close, scope, scopeKey, scopeLabel, organizations, excludedCompanyIds, onSave }) => {
        const t = useComponentsTranslations();
        const modalHeader = useModalContentHeader();
        const [includedYaIds, setIncludedYaIds] = useState<string[]>([]);
        const [buttonGroup, setButtonGroup] = useState<ReactNode>();

        const filteredOrgs = useMemo(
            () => filterOrgsByScopeKey(organizations, scope, scopeKey),
            [organizations, scope, scopeKey]
        );

        const filteredYaIds = useMemo(() => filteredOrgs.map((o) => o.ya_id), [filteredOrgs]);

        const selectOptions: IdOptionType[] = useMemo(
            () =>
                filteredOrgs.map((o) => ({
                    value: o.ya_id,
                    label: o.ya_name,
                    id: o.ya_id,
                })),
            [filteredOrgs]
        );

        const excludedKey = useMemo(
            () => [...excludedCompanyIds].sort().join("\0"),
            [excludedCompanyIds]
        );

        useEffect(() => {
            const ex = new Set(excludedCompanyIds);
            setIncludedYaIds(filteredYaIds.filter((id) => !ex.has(id)));
        }, [filteredYaIds.join("\0"), excludedKey]);

        useLayoutEffect(() => {
            if (!modalHeader) {
                return;
            }
            modalHeader.setSubtitle(
                t("companiesConfigurationModalSubtitle", {
                    label: scopeLabel,
                    included: includedYaIds.length,
                    total: filteredOrgs.length,
                })
            );
        }, [modalHeader, scopeLabel, includedYaIds.length, filteredOrgs.length, t]);

        const onRenderButtonGroup = (node: ReactNode) => setButtonGroup(node);

        const onSaveClick = () => {
            const excluded = filteredYaIds.filter((id) => !includedYaIds.includes(id));
            onSave(excluded);
            close();
        };

        const resetFactor = `${scopeKey}|${selectOptions.length}|${selectOptions.map((o) => o.value).join(",")}`;

        return (
            <div className={styles.container}>
                { filteredOrgs.length === 0 && (
                    <div className={styles.empty}>{t("companiesConfigurationEmpty")}</div>
                )}
                {filteredOrgs.length > 0 && (
                    <>
                        <div className={styles.hint}>{t("companiesConfigurationHint")}</div>
                        <div className={styles.listActionButtons}>{buttonGroup}</div>
                        <SelectList
                            draggable={false}
                            value={includedYaIds}
                            options={selectOptions}
                            resetFactor={resetFactor}
                            renderButtonGroup={onRenderButtonGroup}
                            onChange={setIncludedYaIds}
                            addButtonLabel={t("legalCompanyListAddCompany")}
                        />
                        <div className={styles.buttonGroup}>
                            <Button view="legal" onClick={onSaveClick}>
                                {t("save")}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }
);
